import { UnexpectedError } from '@watershed/errors/UnexpectedError';
/* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
import { RawSQL, dangerousRawSql, sqlF } from '../../sqlTemplate';
import { Unit, unitToString } from '../../unit';
import { ASTExpr, ASTStmt } from '../ast';
import { canBeNull } from '../expr_types';
import { exprToSql } from '../sql';
import { TraceASTAnnotationKind, TraceASTType } from '../trace';
import { convertToType } from '../typeConversion';
import { typecheckExpression } from '../typecheck';
import { Context, SqlExpression, SqlTrace } from '../types';
import { sqlCast } from './cast';

// Turn a line of a program into SQL. Returns:
// - the SqlExpression, and
// - the SqlTrace that should be used when this line is used in a variable in
//   another statement (which is *different* than the SqlExpression's trace!).
//   This will possibly include an annotation from the variable itself.
export function lineToSQL(
  ctx: Context,
  line: ASTStmt
): [SqlExpression, SqlTrace | null] {
  const ty = typecheckExpression(ctx, line.expr);
  let unit: Unit | undefined = ty.ty === 'number' ? ty.unit : undefined;

  const baseTy =
    line.typeDeclaration?.ty === 'nullable'
      ? line.typeDeclaration.baseTy
      : line.typeDeclaration;

  let eSql: SqlExpression;
  if (line.typeDeclaration) {
    eSql = convertToType(ctx, {
      fromExpr: line.expr,
      toType: line.typeDeclaration,
    }).toSql();
    unit = baseTy?.ty === 'number' ? baseTy.unit : undefined;
  } else {
    eSql = exprToSql(ctx, line.expr);
  }

  if (canBeNull(ty) && line.typeDeclaration) {
    // If the type of the RHS can be null, there's a chance it's an untyped SQL
    // NULL, which DuckDB will treat as a NULL::INT32. If folks downstream from
    // us are expecting e.g., a string, this will fail in an exciting way.
    eSql = {
      expr: sqlCast(eSql.expr, line.typeDeclaration),
      stage: eSql.stage,
      unitExpr: eSql.unitExpr,
      error: eSql.error,
      trace: eSql.trace,
    };
  }

  if (eSql.trace === null) {
    return [eSql, null];
  }

  // Special case: for passthrough assignments (like `x = y`), don't add
  // additional annotations, since nothing interesting is happening to the
  // value. We believe the innermost annotation is going to be the most
  // interesting and descriptive one anyways!
  //
  // In theory we could filter these useless annotations out at read time, but
  // it turns out there are a lot of them, and generating them is pretty costly
  if (exprIsPassthroughAssignment(line.expr)) {
    return [eSql, eSql.trace];
  }

  let unitF: RawSQL | string | null;
  if (unit) {
    unitF = unitToString(unit);
  } else if (ty.ty === 'quantity') {
    // TODO(carl): we don't allow quantities at the top level, but I can't
    // find where we enforce it! But it's simple enough to copy and paste the
    // code that *would* support it, since I already wrote it in traceHelper
    UnexpectedError.invariant(eSql.unitExpr, 'quantities must have unit exprs');
    unitF = dangerousRawSql(eSql.unitExpr);
  } else {
    unitF = null;
  }

  const value = dangerousRawSql(eSql.expr);
  const trace = {
    expr: sqlF`json_object('t',${TraceASTType.TAnnotation},'v',round(${value},8),'u',${unitF},'k',${TraceASTAnnotationKind.TVariable},'n',${line.name},'e',${eSql.trace.expr})`,
    fingerprintExpr: eSql.trace.fingerprintExpr,
  };
  return [eSql, trace];
}

function exprIsPassthroughAssignment(expr: ASTExpr): boolean {
  return expr.t === 'identifier';
}
